import { useQuery } from '@tanstack/react-query';
import { Shield, AlertTriangle, CheckCircle, DollarSign, Loader2, Eye, FileWarning } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

const fmtCurrency = (v: number) => `$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const STATUS_STYLES: Record<string, { label: string; variant: 'default' | 'destructive' | 'secondary' | 'outline' }> = {
  clean: { label: 'Clean', variant: 'default' },
  review_needed: { label: 'Review Needed', variant: 'secondary' },
  high_risk: { label: 'High Risk', variant: 'destructive' },
  no_data: { label: 'No Data', variant: 'outline' },
};

const RISK_COLORS: Record<string, string> = {
  high: 'text-red-500 bg-red-500/10',
  medium: 'text-amber-500 bg-amber-500/10',
  low: 'text-blue-500 bg-blue-500/10',
};

export default function FBTDetectorPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['fbt-scan'],
    queryFn: async () => { const r = await apiClient.get('/compliance/fbt'); return r.data.data; },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Scanning expenses for fringe benefits...</p>
        </div>
      </div>
    );
  }

  const summary = data?.summary;
  const clients = data?.clients || [];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Eye}
        title="FBT Detector"
        description="Scans accounting expenses for potential Fringe Benefits Tax liabilities"
      />


      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center"><CheckCircle className="h-5 w-5 text-green-500" /></div>
                <div><p className="text-2xl font-bold">{summary.clean}</p><p className="text-xs text-muted-foreground">Clean</p></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center"><FileWarning className="h-5 w-5 text-amber-500" /></div>
                <div><p className="text-2xl font-bold">{summary.reviewNeeded}</p><p className="text-xs text-muted-foreground">Review Needed</p></div>
              </div>
            </CardContent>
          </Card>
          <Card className={summary.highRisk > 0 ? 'border-red-500/30' : ''}>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-red-500" /></div>
                <div><p className="text-2xl font-bold">{summary.highRisk}</p><p className="text-xs text-muted-foreground">High Risk</p></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><DollarSign className="h-5 w-5 text-primary" /></div>
                <div><p className="text-2xl font-bold">{fmtCurrency(summary.totalEstimatedFBT)}</p><p className="text-xs text-muted-foreground">Est. FBT Liability</p></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Client Results */}
      {clients.filter((c: any) => c.status !== 'no_data').map((client: any) => (
        <Card key={client.clientId} className={client.status === 'high_risk' ? 'border-red-500/30' : ''}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">{client.clientName}</CardTitle>
                <CardDescription className="text-xs">{client.financialYear} | {client.totalExpensesScanned} expense lines scanned</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {client.estimatedFBTLiability > 0 && (
                  <span className="text-sm font-medium text-primary">{fmtCurrency(client.estimatedFBTLiability)}</span>
                )}
                <Badge variant={STATUS_STYLES[client.status]?.variant || 'outline'} className="text-[10px]">
                  {STATUS_STYLES[client.status]?.label || client.status}
                </Badge>
              </div>
            </div>
          </CardHeader>
          {client.potentialFBTItems.length > 0 && (
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Expense</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">FBT Category</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Amount</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Est. FBT</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {client.potentialFBTItems.map((item: any, i: number) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-muted/20 transition-colors group">
                      <td className="px-4 py-2.5">
                        <span className="font-medium">{item.expenseLabel}</span>
                        <p className="text-[10px] text-muted-foreground mt-0.5 hidden group-hover:block">{item.exemptionNotes}</p>
                      </td>
                      <td className="px-4 py-2.5 text-xs">
                        {item.category}
                        <span className="text-muted-foreground ml-1">({item.fbtType})</span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono">{fmtCurrency(item.amount)}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-medium">{fmtCurrency(item.estimatedFBT)}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className={cn('text-[9px]', RISK_COLORS[item.risk])}>{item.risk}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {client.recommendations.length > 0 && (
                <div className="px-4 py-3 border-t bg-muted/10">
                  {client.recommendations.map((r: string, i: number) => (
                    <p key={i} className="text-xs text-muted-foreground">{r}</p>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      ))}

      {clients.filter((c: any) => c.status !== 'no_data').length === 0 && (
        <Card>
          <CardContent className="pt-12 pb-12 text-center text-muted-foreground">
            <Eye className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>No connected clients with expense data found.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
